import { create } from "zustand";
import client from "apollo/client";
import { gql } from "@apollo/client";
import { devtools } from "zustand/middleware";
import { getCurrentUser } from "aws-amplify/auth";

import { listUserHorses } from "apollo/queries/horses";
import { LIST_DISCIPLINES } from "apollo/queries/disciplines";
import { UPDATE_HORSE, DELETE_HORSES } from "apollo/mutations/horses";

type Horse = {
  id: string;
  [key: string]: any;
};

type Discipline = {
  id?: string;
  name?: string;
  [key: string]: any;
};

interface HorseStore {
  horses: Horse[];
  nextToken: string | null;
  limit: number;

  listDisciplines: Discipline[];
  listDisciplinesRaw: any;

  horseName: string;
  horseData: any;
  deleteHorses: string[];
  selectAll: boolean;

  loading: boolean;
  isHorsesLoading: boolean;
  isHorseUpdating: boolean;
  isDeleting: boolean;
  isDeletedSuccessfully: boolean;

  error: string | null;
  updateHorseError: string | null;

  setHorseName: (v: string) => void;
  setHorseData: (v: any) => void;
  setDeleteHorses: (
    value: string[] | ((prev: string[]) => string[])
  ) => void;
  setSelectAll: (v: boolean) => void;
  setIsDeleting: (v: boolean) => void;
  setIsHorseUpdating: (v: boolean) => void;
  setIsDeletedSuccessfully: (v: boolean) => void;

  fetchHorseData: () => Promise<void>;
  loadMoreHorses: () => Promise<void>;
  refetchListDisciplines: () => Promise<void>;
  updateHorse: (variables: Record<string, any>) => Promise<any>;
  deleteHorse: (variables: Record<string, any>) => Promise<any>;

  setError: (msg: string | null) => void;
}

const LIST_QUERY = gql(listUserHorses);
const LIST_DISC_QUERY = gql(LIST_DISCIPLINES);
const UPDATE_HORSE_MUT = gql(UPDATE_HORSE);
const DELETE_HORSES_MUT = gql(DELETE_HORSES);

type UpdateHorseVariables = { input: { id: string } & Record<string, any> };
type UpdateHorseArg = UpdateHorseVariables | { variables: UpdateHorseVariables };

type DeleteIdsVars = { ids: string[] };
type DeleteArg =
  | DeleteIdsVars
  | { variables: DeleteIdsVars }
  | { variables: { input: { ids: string[] } } }
  | { ids: string[]; refetchQueries?: any }
  | { variables: DeleteIdsVars; refetchQueries?: any }
  | { variables: { input: { ids: string[] } }; refetchQueries?: any };

function normalizeIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === "string" && v.trim().length > 0);
}

export const useHorseStore = create<HorseStore>()(
  devtools((set, get) => ({
    horses: [],
    nextToken: null,
    limit: 5,

    listDisciplines: [],
    listDisciplinesRaw: null,

    horseName: "",
    horseData: "",
    deleteHorses: [],
    selectAll: false,

    loading: false,
    isHorsesLoading: false,
    isHorseUpdating: false,
    isDeleting: false,
    isDeletedSuccessfully: false,

    error: null,
    updateHorseError: null,

    setHorseName: (v) => set({ horseName: v }),
    setHorseData: (v) => set({ horseData: v }),
    setDeleteHorses: (ids) =>
      set((state) => ({
        deleteHorses: typeof ids === "function" ? (ids as (prev: string[]) => string[])(state.deleteHorses) : ids,
      })),
    setSelectAll: (v) => set({ selectAll: v }),
    setIsDeleting: (v) => set({ isDeleting: v }),
    setIsHorseUpdating: (v) => set({ isHorseUpdating: v }),
    setIsDeletedSuccessfully: (v) => set({ isDeletedSuccessfully: v }),
    setError: (msg) => set({ error: msg }),

    fetchHorseData: async () => {
      try {
        set({ loading: true, error: null });
        const { username } = await getCurrentUser();
        if (!username) {
          set({ loading: false });
          return;
        }

        const { limit } = get();
        const { data } = await client.query({
          query: LIST_QUERY,
          variables: { limit, nextToken: null },
          fetchPolicy: "network-only",
        });

        const list = data?.listUserHorses?.items ?? [];
        const nextToken = data?.listUserHorses?.nextToken ?? null;

        set({
          horses: list,
          nextToken,
          loading: false,
        });
      } catch (e: any) {
        set({ error: e?.message ?? "Failed to load horses", loading: false });
      }
    },

    loadMoreHorses: async () => {
      const { nextToken, limit, horses } = get();
      if (!nextToken) return;

      try {
        set({ isHorsesLoading: true });
        const { data } = await client.query({
          query: LIST_QUERY,
          variables: { limit, nextToken },
          fetchPolicy: "network-only",
        });

        const more = data?.listUserHorses?.items ?? [];
        const next = data?.listUserHorses?.nextToken ?? null;

        set({
          horses: [...horses, ...more],
          nextToken: next,
          isHorsesLoading: false,
        });
      } catch (e: any) {
        set({
          error: e?.message ?? "Failed to load more horses",
          isHorsesLoading: false,
        });
      }
    },

    refetchListDisciplines: async () => {
      try {
        const { data } = await client.query({
          query: LIST_DISC_QUERY,
          fetchPolicy: "network-only",
          errorPolicy: "all",
        });
        const items: Discipline[] = data?.listDisciplines ?? [];
        set({ listDisciplines: items, listDisciplinesRaw: data });
      } catch (e: any) {
        set({ error: e?.message ?? "Failed to load disciplines" });
      }
    },

    updateHorse: async (arg: UpdateHorseArg) => {
      try {
        set({ isHorseUpdating: true, updateHorseError: null });

        const variables: UpdateHorseVariables =
          "variables" in arg ? arg.variables : arg;

        const res = await client.mutate({
          mutation: UPDATE_HORSE_MUT,
          variables,
          update: (cache, { data }) => {
            const cached = cache.readQuery({
              query: LIST_QUERY,
              variables: { limit: get().limit, nextToken: null },
            }) as any;

            const updated = data?.updateHorse;
            if (cached && updated) {
              const mapped =
                cached?.listUserHorses?.items?.map((h: Horse) =>
                  h?.id === updated?.id ? updated : h
                ) ?? [];

              cache.writeQuery({
                query: LIST_QUERY,
                variables: { limit: get().limit, nextToken: null },
                data: {
                  listUserHorses: {
                    ...cached.listUserHorses,
                    items: mapped,
                  },
                },
              });
            }
          },
        });

        const updated = res?.data?.updateHorse as Horse | undefined;
        if (updated) {
          set((s) => ({
            horses: s.horses.map((h) => (h.id === updated.id ? updated : h)),
          }));
        }

        return res;
      } catch (e: any) {
        set({
          updateHorseError: e?.message ?? "Failed to update horse",
        });
        throw e;
      } finally {
        set({ isHorseUpdating: false });
      }
    },

    deleteHorse: async (arg: DeleteArg) => {
      const passed = "variables" in (arg as any) ? (arg as any).variables : arg;
      const refetchQueries = "variables" in (arg as any) ? (arg as any).refetchQueries : (arg as any).refetchQueries;

      const ids = normalizeIds(
        (passed as any)?.ids ?? (passed as any)?.input?.ids
      );

      if (ids.length === 0) {
        set({ error: "No valid horse IDs to delete.", isDeleting: false, isDeletedSuccessfully: false });
        throw new Error("No valid horse IDs to delete.");
      }

      try {
        set({ isDeleting: true, isDeletedSuccessfully: false, error: null });

        const res = await client.mutate({
          mutation: DELETE_HORSES_MUT,
          variables: { ids },
          refetchQueries,
        });

        const payload = res?.data?.deleteHorse;
        const returnedIds: string[] = Array.isArray(payload)
          ? payload.map((x: any) => x?.id).filter(Boolean)
          : payload?.id ? [payload.id] : ids;

        set((s) => ({
          horses: s.horses.filter((h) => !returnedIds.includes(h.id)),
          deleteHorses: s.deleteHorses.filter((id) => !returnedIds.includes(id)),
        }));

        const cached = client.readQuery({
          query: LIST_QUERY,
          variables: { limit: get().limit, nextToken: null },
        }) as any;

        if (cached?.listUserHorses?.items) {
          client.writeQuery({
            query: LIST_QUERY,
            variables: { limit: get().limit, nextToken: null },
            data: {
              listUserHorses: {
                ...cached.listUserHorses,
                items: cached.listUserHorses.items.filter(
                  (h: Horse) => !returnedIds.includes(h.id)
                ),
              },
            },
          });
        }

        set({ isDeleting: false, isDeletedSuccessfully: true });
        return res;
      } catch (e: any) {
        set({
          isDeleting: false,
          isDeletedSuccessfully: false,
          error: e?.message ?? "Failed to delete horses",
        });
        throw e;
      }
    },
  }))
);
